import React, { useEffect, useMemo, useState } from 'react';
import {
  confirmRelationship,
  createManualRelationship,
  rejectRelationship,
} from '../services/itemRelationshipService';
import { detachOptionToNewItem } from '../services/itemCrossReferenceService';
import {
  RELATIONSHIP_TYPE_LABELS,
  buildRelationshipGroups,
  isSecondaryWorkspaceType,
  secondaryRelationshipMetrics,
} from '../utils/relatedProductCatalog';
import {
  buildComparablePriceDisplay,
  comparablePrecision,
  formatComparablePrice,
} from '../utils/priceComparison';
import { HelpInfoButton } from './HelpTooltip';
import { buildWorkingSetGroups, expandOptionsAcrossGroup } from '../utils/relatedItemGroups';

const text = (value) => String(value ?? '').trim();
const PICKER_LIMIT = 60;
const TYPE_OPTIONS = [
  { value: 'purchasing_alternative', label: 'Alternative products' },
  { value: 'same_family', label: 'Similar / same family' },
];

const styles = {
  shell: { display: 'grid', gap: 16, color: '#3D2518' },
  intro: {
    border: '1px solid #E8DDD4', borderRadius: 14, background: '#FAF7F4', padding: '14px 15px',
  },
  title: { color: '#3E2723', fontWeight: 800, fontSize: 18, margin: 0 },
  hint: { color: '#7A6A62', fontSize: 12, margin: '4px 0 0', lineHeight: 1.45 },
  tabs: { display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 13 },
  tab: {
    border: '1.5px solid #D9CEC4', borderRadius: 999, background: '#fff', color: '#6B5A52',
    minHeight: 34, padding: '6px 12px', fontSize: 12, fontWeight: 750, cursor: 'pointer',
  },
  activeTab: { background: '#5A4A42', border: '1.5px solid #5A4A42', color: '#fff' },
  count: {
    display: 'inline-flex', minWidth: 20, justifyContent: 'center', padding: '1px 6px', marginLeft: 5,
    borderRadius: 999, background: 'rgba(255,255,255,.22)', fontSize: 10, fontWeight: 800,
  },
  // The suggestion queue is deliberately NOT a peer of the catalog tabs — it
  // holds unreviewed machine guesses, and presenting it alongside confirmed
  // relationships is what made the view read as "some sort of suggestions".
  reviewCta: {
    border: '1.5px solid #C9A227', borderRadius: 999, background: '#FFF8E5', color: '#7A5B00',
    minHeight: 34, padding: '6px 13px', fontSize: 12, fontWeight: 800, cursor: 'pointer',
    marginLeft: 'auto',
  },
  headline: {
    display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap', marginTop: 13,
  },
  headlineFigure: {
    display: 'inline-flex', alignItems: 'baseline', gap: 5, padding: '5px 11px', borderRadius: 999,
    background: '#fff', border: '1px solid #E2D6CB', color: '#6B5A52', fontSize: 12, fontWeight: 700,
  },
  headlineNumber: { color: '#3E2723', fontSize: 15, fontWeight: 850 },
  backLink: {
    border: 0, background: 'none', color: '#5A4A42', fontSize: 12, fontWeight: 750,
    padding: 0, cursor: 'pointer', textDecoration: 'underline',
  },
  suggestionIntro: {
    border: '1px solid #EBD9A6', borderRadius: 12, background: '#FFFBF0', padding: '11px 13px',
    color: '#6F5A22', fontSize: 12, lineHeight: 1.5, marginBottom: 12,
  },
  toolbar: {
    display: 'flex', alignItems: 'end', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap',
  },
  searchWrap: { flex: '1 1 300px', maxWidth: 560 },
  label: {
    display: 'block', color: '#6B5A52', fontSize: 10, fontWeight: 800,
    textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 4,
  },
  summary: { color: '#8B7B73', fontSize: 12, paddingBottom: 8 },
  list: { display: 'grid', gap: 11 },
  card: {
    border: '1px solid #E4D9D0', borderRadius: 13, background: '#fff', overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(62,39,35,.045)',
  },
  suggestionCard: { border: '1px dashed #CFC3DD', background: '#FCFAFE' },
  cardHead: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12,
    padding: '12px 13px 9px', flexWrap: 'wrap',
  },
  cardTitle: { color: '#3E2723', fontSize: 15, fontWeight: 800, margin: 0 },
  cardMeta: { color: '#8B7B73', fontSize: 11, marginTop: 2 },
  badgeRow: { display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap', marginTop: 5 },
  badge: {
    display: 'inline-flex', padding: '2px 7px', borderRadius: 999, background: '#EEF3F5',
    color: '#526B7C', fontSize: 9, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.04em',
  },
  suggestionBadge: { background: '#EDE7F6', color: '#5E35B1' },
  cardActions: { display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' },
  members: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
    gap: 8, padding: '0 13px 13px',
  },
  member: {
    display: 'grid', gridTemplateColumns: '28px minmax(0, 1fr) auto', alignItems: 'start',
    gap: 8, border: '1px solid #EEE5DF', borderRadius: 10, background: '#FBF8F5', padding: '8px 9px',
  },
  memberBody: { minWidth: 0, height: '100%' },
  letter: {
    display: 'inline-flex', width: 25, height: 25, borderRadius: 7, alignItems: 'center',
    justifyContent: 'center', background: '#EDE4DC', color: '#5A4A42', fontSize: 10, fontWeight: 900,
  },
  itemName: { color: '#3E2723', fontSize: 12.5, fontWeight: 750, lineHeight: 1.25 },
  itemMeta: { color: '#7D6D64', fontSize: 10.5, marginTop: 3, lineHeight: 1.35 },
  vendorPrices: { display: 'grid', gap: 5, marginTop: 7 },
  vendorPrice: {
    borderTop: '1px solid #E9DFD7', paddingTop: 5, color: '#5A4A42', fontSize: 10.5,
  },
  comparisonItem: { display: 'flex', flexDirection: 'column', height: '100%' },
  comparisonVendorPrices: { display: 'flex', flex: '1 1 auto' },
  comparisonVendorPrice: { display: 'flex', flexDirection: 'column', width: '100%' },
  comparisonPriceFacts: { marginTop: 'auto', paddingTop: 3 },
  vendorPriceTop: { display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' },
  vendorName: { color: '#3E2723', fontWeight: 750 },
  vendorProduct: { color: '#7D6D64', marginTop: 1, lineHeight: 1.3 },
  priceFacts: { display: 'grid', gap: 2, marginTop: 3 },
  packageFact: { display: 'flex', gap: 5, alignItems: 'baseline', flexWrap: 'wrap' },
  comparableFact: { minHeight: 15 },
  packagePrice: { color: '#5A4A42', fontWeight: 700 },
  comparablePrice: { color: '#2E6B3B', fontWeight: 800 },
  lowestPrice: {
    display: 'inline-flex', padding: '1px 6px', borderRadius: 999, background: '#E8F5E9',
    color: '#2E7D32', fontSize: 8.5, fontWeight: 850, textTransform: 'uppercase',
    letterSpacing: '.04em', whiteSpace: 'nowrap',
  },
  preferredPrice: {
    display: 'inline-flex', padding: '1px 5px', borderRadius: 999, background: '#FFF4E0',
    color: '#8A5A00', fontSize: 8.5, fontWeight: 800, textTransform: 'uppercase',
  },
  // One line per suggested ROW: the proposed pair, its proposed
  // classification, the model's confidence, and the reasoning that came with
  // it. The rows are the suggestion — hiding them left "good reasoning"
  // invisible everywhere.
  reasonList: { display: 'grid', gap: 7, padding: '0 13px 13px' },
  reason: {
    border: '1px solid #EEE5DF', borderRadius: 10, background: '#FBF8F5', padding: '8px 10px',
  },
  reasonPair: {
    display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
    color: '#3E2723', fontSize: 11.5, fontWeight: 750,
  },
  reasonConfidence: { color: '#8B7B73', fontSize: 10, fontWeight: 700 },
  reasonText: { color: '#6B5A52', fontSize: 11, lineHeight: 1.45, marginTop: 4 },
  acceptAsSelect: { width: 'auto', minHeight: 31, fontSize: 12 },
  sectionHeading: {
    color: '#3E2723', fontSize: 13, fontWeight: 800, margin: 0,
    display: 'flex', alignItems: 'center', gap: 7,
    textTransform: 'uppercase', letterSpacing: '.05em',
  },
  sectionCount: {
    display: 'inline-flex', minWidth: 20, justifyContent: 'center', padding: '1px 7px',
    borderRadius: 999, background: '#EDE4DC', color: '#5A4A42', fontSize: 10.5, fontWeight: 800,
  },
  sectionHint: { color: '#8B7B73', fontSize: 11, margin: '3px 0 9px' },
  sections: { display: 'grid', gap: 18 },
  editor: { borderTop: '1px solid #EEE5DF', background: '#FAF7F4', padding: '11px 13px 13px' },
  editorHead: { display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', flexWrap: 'wrap' },
  picker: { marginTop: 9, border: '1px solid #E5DAD2', borderRadius: 11, background: '#fff', padding: 10 },
  pickerList: { display: 'grid', gap: 5, maxHeight: 260, overflowY: 'auto', marginTop: 7 },
  pickerResult: {
    width: '100%', border: '1px solid #EAE1DA', borderRadius: 8, background: '#fff', color: '#3D2518',
    padding: '8px 9px', display: 'block',
    alignItems: 'center', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit',
  },
  pickerResultSelected: { border: '1px solid #8A5A00', background: '#FFF8EA' },
  pickerFooter: { color: '#8B7B73', fontSize: 10.5, marginTop: 6 },
  empty: {
    border: '1px solid #E6DDD6', borderRadius: 12, background: '#FAF7F4',
    padding: '18px 16px', color: '#6F6259', fontSize: 12,
  },
  error: { background: '#FDECEA', border: '1px solid #F5C2BD', color: '#B71C1C', borderRadius: 8, padding: '8px 10px', fontSize: 12 },
  notice: { background: '#E8F5E9', border: '1px solid #C8E6C9', color: '#2E7D32', borderRadius: 8, padding: '8px 10px', fontSize: 12 },
  createCard: { border: '1px solid #E4D9D0', borderRadius: 13, background: '#fff', padding: 14 },
  createGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 },
  createFooter: {
    borderTop: '1px solid #EEE5DF', marginTop: 13, paddingTop: 12,
    display: 'flex', gap: 9, alignItems: 'end', justifyContent: 'flex-end', flexWrap: 'wrap',
  },
};

const preferredOption = (itemId, optionsByItemId) => {
  const options = (optionsByItemId?.[itemId] || []).filter((option) => option?.status !== 'inactive');
  return options.find((option) => option.is_preferred === true) || options[0] || null;
};

const itemVendor = (item, optionsByItemId, vendorNameById) => {
  const option = preferredOption(item?.id, optionsByItemId);
  return text(vendorNameById?.[option?.vendor_id])
    || text(option?.vendor_name_text)
    || text(item?.vendor)
    || 'Vendor not set';
};

const itemUnit = (item, optionsByItemId) => {
  const option = preferredOption(item?.id, optionsByItemId);
  return text(item?.unit) || text(option?.purchase_unit) || 'Unit not set';
};

const activeOptionsForItem = (itemId, optionsByItemId) => (
  (optionsByItemId?.[itemId] || []).filter((option) => option && option.status !== 'inactive')
);

const itemSearchText = (item, optionsByItemId, vendorNameById) => {
  const optionTerms = activeOptionsForItem(item?.id, optionsByItemId).flatMap((option) => [
    vendorNameById?.[option.vendor_id],
    option.vendor_name_text,
    option.vendor_description,
    option.vendor_sku,
    option.purchase_unit,
    option.pack_description,
    option.package_price,
  ]);
  return [
    item?.description,
    item?.brand,
    item?.category,
    item?.location,
    itemVendor(item, optionsByItemId, vendorNameById),
    itemUnit(item, optionsByItemId),
    ...optionTerms,
  ].map((value) => text(value).toLowerCase()).join(' ');
};

const alphabeticalItems = (items) => (Array.isArray(items) ? items : []).slice().sort((left, right) => (
  (text(left?.description) || `Item ${left?.id}`).localeCompare(
    text(right?.description) || `Item ${right?.id}`,
    undefined,
    { sensitivity: 'base', numeric: true },
  )
));

const relationshipLetter = (index) => {
  if (index < 26) return String.fromCharCode(65 + index);
  return `A${index - 25}`;
};

const sameIds = (left, right) => {
  const leftKeys = (left || []).map(String).sort();
  const rightKeys = (right || []).map(String).sort();
  return leftKeys.length === rightKeys.length && leftKeys.every((key, index) => key === rightKeys[index]);
};

// Adding a member to a group extends the relationship the group already IS,
// so its own type wins. TYPE_OPTIONS is only the menu offered when a person
// creates a relationship by hand; using it as the whitelist here silently
// rewrote a `same_item` group's new rows as `purchasing_alternative`, and now
// that groups are built per type, the coerced row landed in a different card
// than the one being edited. Only a genuinely unknown type falls back.
const STORED_RELATIONSHIP_TYPES = ['same_item', 'purchasing_alternative', 'same_family'];
const relationshipTypeForGroup = (group) => (
  STORED_RELATIONSHIP_TYPES.includes(group?.relationshipType)
    ? group.relationshipType
    : 'purchasing_alternative'
);

// Classification precedence when deciding whether a suggestion is still
// news. Narrower, more actionable types outrank broader ones: a pair already
// confirmed as ALTERNATIVES needs no alternative re-ask, but a pair confirmed
// only as SAME FAMILY still deserves its alternative (or equivalent)
// proposal — family is browsing metadata and must never swallow a narrower
// link. The store-416 maraschino cherries were the live failure: two good
// alternative suggestions rendered nowhere because a confirmed family blob
// (via Luxardo Liqueur) contained both endpoints and the old filter dropped
// any suggestion whose pair was confirmed together under ANY type.
const TYPE_RANK = { same_family: 1, purchasing_alternative: 2, same_item: 3 };

// What a suggestion may be confirmed AS. Superset of TYPE_OPTIONS (manual
// creation deliberately offers no same_item; reclassifying a suggestion to
// "these are one item" is exactly how an over-cautious alternative proposal
// becomes an equivalent link).
const RECLASSIFY_OPTIONS = [
  { value: 'same_item', label: 'Equivalent — same item' },
  ...TYPE_OPTIONS,
];

// The sections of the confirmed list, in reading order. Alternative products
// and Same family are different claims (domain model: alternatives are
// substitutes with a difference you may care about; family is browsing
// metadata) and must never read as one undifferentiated pile.
//
// There is deliberately no "Equivalent — grouped items" section any more. A
// confirmed same_item link IS a Related Products group; RelatedProductGroupsView
// folds and renders it as one card with its vendors, prices and comparable
// unit. Listing the same rows again here made this workspace a second, worse
// view of finished data. The rows themselves are untouched in the database —
// they still build the equivalent working set that this view's own concept
// collapsing, suggestion suppression and canonical election depend on.
const EXISTING_SECTIONS = [
  ['purchasing_alternative', 'Alternative products',
    'Confirmed substitutes — a different product that can stand in for this one.'],
  ['same_family', 'Same family',
    'Broader family links for browsing — related products, not substitutes.'],
];

const pairKey = (left, right) => [String(left), String(right)].sort().join('|');

const vendorPriceMeta = (item, optionsByItemId, vendorNameById) => {
  const options = activeOptionsForItem(item?.id, optionsByItemId);
  const optionById = new Map(options.map((option) => [String(option.id), option]));
  const display = buildComparablePriceDisplay({ item: item || {}, options, vendorNameById });
  const rows = display.options.map((row) => {
    const option = optionById.get(String(row.optionId)) || {};
    return {
      ...row,
      key: `${item?.id}:${row.optionId}`,
      isPreferred: option.is_preferred === true,
      purchaseUnit: text(option.purchase_unit) || text(item?.unit) || 'package',
      // The vendor's REAL pack wording, verbatim. Two products can share a
      // name, a vendor and a purchase unit and still be different buys; the
      // pack is often the only thing on the row that tells them apart.
      packDescription: text(option.pack_description),
      comparableLabel: 'comparable',
    };
  }).sort((left, right) => {
    if (left.isPreferred !== right.isPreferred) return left.isPreferred ? -1 : 1;
    return left.vendorName.localeCompare(right.vendorName, undefined, { sensitivity: 'base' });
  });

  if (rows.length) return { display, rows, fromPurchaseOptions: true };

  const legacyPackagePrice = formatComparablePrice(item?.case_price, null);
  const legacyUnitPrice = formatComparablePrice(item?.unit_price, text(item?.unit) || null);
  return {
    display,
    fromPurchaseOptions: false,
    rows: [{
      key: `${item?.id}:legacy`,
      optionId: null,
      vendorName: itemVendor(item, optionsByItemId, vendorNameById),
      productName: text(item?.description) || `Item ${item?.id}`,
      purchaseUnit: text(item?.unit) || 'package',
      packagePriceDisplay: legacyPackagePrice,
      purchaseUnitLabel: `per ${text(item?.unit) || 'package'}`,
      comparableDisplay: legacyUnitPrice,
      comparableLabel: 'inventory unit price',
      comparableUnitCost: null,
      isLowest: false,
      isTiedLowest: false,
      isPreferred: false,
      needsReview: false,
    }],
  };
};

const groupVendorPriceMeta = (members, optionsByItemId, vendorNameById) => {
  const byItemId = new Map();
  const comparable = [];
  const unitKeys = new Set();
  let comparisonBlocked = false;
  (members || []).forEach((item) => {
    const meta = vendorPriceMeta(item, optionsByItemId, vendorNameById);
    byItemId.set(String(item.id), meta);
    if (meta.rows.some((row) => row.packagePriceDisplay && (
      !meta.fromPurchaseOptions
      || !meta.display.comparisonUnitLabel
      || !Number.isFinite(row.comparableUnitCost)
      || row.comparableUnitCost <= 0
    ))) comparisonBlocked = true;
    if (!meta.fromPurchaseOptions || !meta.display.comparisonUnitLabel) return;
    const unitKey = text(meta.display.comparisonUnitLabel).toLowerCase();
    meta.rows.forEach((row) => {
      if (!Number.isFinite(row.comparableUnitCost) || row.comparableUnitCost <= 0) return;
      unitKeys.add(unitKey);
      comparable.push(row);
    });
  });

  const lowestKeys = new Set();
  if (comparable.length && unitKeys.size === 1) {
    const comparisonUnit = [...unitKeys][0];
    const { decimals } = comparablePrecision(comparable.map((row) => row.comparableUnitCost));
    byItemId.forEach((meta) => {
      meta.rows = meta.rows.map((row) => ({
        ...row,
        comparableDisplay: Number.isFinite(row.comparableUnitCost) && row.comparableUnitCost > 0
          ? formatComparablePrice(row.comparableUnitCost, comparisonUnit, { decimals })
          : row.comparableDisplay,
      }));
    });
  }
  if (!comparisonBlocked && comparable.length >= 2 && unitKeys.size === 1) {
    const lowest = Math.min(...comparable.map((row) => row.comparableUnitCost));
    comparable.forEach((row) => {
      if (row.comparableUnitCost === lowest) lowestKeys.add(row.key);
    });
  }
  // The relationship card's A/B/C members are vendor products, not canonical
  // items with an arbitrary number of vendor products nested inside one
  // letter. Comparable products lead the row from least to most expensive;
  // unresolved prices stay visible at the end.
  const products = (members || []).flatMap((item) => (
    (byItemId.get(String(item.id))?.rows || []).map((row) => ({ item, row }))
  )).sort((left, right) => {
    const leftCost = Number.isFinite(left.row.comparableUnitCost) && left.row.comparableUnitCost > 0
      ? left.row.comparableUnitCost
      : Number.POSITIVE_INFINITY;
    const rightCost = Number.isFinite(right.row.comparableUnitCost) && right.row.comparableUnitCost > 0
      ? right.row.comparableUnitCost
      : Number.POSITIVE_INFINITY;
    if (leftCost !== rightCost) return leftCost - rightCost;
    const itemOrder = (text(left.item.description) || `Item ${left.item.id}`).localeCompare(
      text(right.item.description) || `Item ${right.item.id}`,
      undefined,
      { sensitivity: 'base', numeric: true },
    );
    if (itemOrder !== 0) return itemOrder;
    return text(left.row.vendorName).localeCompare(text(right.row.vendorName), undefined, { sensitivity: 'base' });
  });
  return { byItemId, lowestKeys, products };
};

const ItemMeta = ({ item, optionsByItemId, vendorNameById, priceMeta = null }) => {
  const meta = priceMeta || vendorPriceMeta(item, optionsByItemId, vendorNameById);
  const groupComparison = priceMeta !== null;
  return (
    <div style={groupComparison ? styles.comparisonItem : undefined}>
      <div style={styles.itemName}>{text(item?.description) || `Item ${item?.id}`}</div>
      <div style={styles.itemMeta}>
        Inventory unit: {itemUnit(item, optionsByItemId)}
        {text(item?.category) ? ` · ${text(item.category)}` : ''}
      </div>
      <div
        style={{ ...styles.vendorPrices, ...(groupComparison ? styles.comparisonVendorPrices : {}) }}
        data-testid="relationship-vendor-prices"
      >
        {meta.rows.map((row) => {
          const isLowest = groupComparison
            ? meta.lowestKeys?.has(row.key)
            : meta.rows.length > 1 && (row.isLowest || row.isTiedLowest);
          const tied = isLowest && (groupComparison ? meta.lowestKeys.size > 1 : row.isTiedLowest);
          const distinctProduct = text(row.productName)
            && text(row.productName).toLowerCase() !== text(item?.description).toLowerCase();
          return (
            <div
              key={row.key}
              style={{ ...styles.vendorPrice, ...(groupComparison ? styles.comparisonVendorPrice : {}) }}
              data-testid="relationship-vendor-price"
            >
              <div style={styles.vendorPriceTop}>
                <span style={styles.vendorName}>{row.vendorName}</span>
                {row.isPreferred && <span style={styles.preferredPrice}>Preferred</span>}
                {isLowest && (
                  <span style={styles.lowestPrice} data-testid="lowest-comparable-price">
                    {tied ? 'Tied lowest' : 'Lowest comparable price'}
                  </span>
                )}
              </div>
              {distinctProduct && <div style={styles.vendorProduct}>{row.productName}</div>}
              <div
                style={{ ...styles.priceFacts, ...(groupComparison ? styles.comparisonPriceFacts : {}) }}
                data-testid="relationship-price-facts"
              >
                <div style={styles.packageFact} data-testid="relationship-package-price">
                  <span>Unit: {row.purchaseUnit}</span>
                  {row.packDescription && (
                    <>
                      <span aria-hidden="true">·</span>
                      <span>Pack: {row.packDescription}</span>
                    </>
                  )}
                  <span aria-hidden="true">·</span>
                  <span style={styles.packagePrice}>
                    {row.packagePriceDisplay ? `${row.packagePriceDisplay} ${row.purchaseUnitLabel}` : 'Price not set'}
                  </span>
                </div>
                <div style={styles.comparableFact} data-testid="relationship-comparable-price">
                  {row.comparableDisplay && (
                    <span style={styles.comparablePrice}>{row.comparableDisplay} {row.comparableLabel}</span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * The catalog search control every relationship surface uses to pick a
 * product: alphabetical by default, searchable by item name, brand, category,
 * location, vendor, unit, vendor product description, SKU, pack and price
 * (itemSearchText), and rendering enough of each result to tell two similar
 * products apart. Exported so the Related Products group editor adds members
 * with the same picker rather than growing a third one.
 */
export const RelationshipItemPicker = ({
  idPrefix,
  items,
  optionsByItemId,
  vendorNameById,
  query,
  onQueryChange,
  selectedId,
  onPick,
  excludeIds = [],
}) => {
  const excluded = new Set((excludeIds || []).map(String));
  const normalizedQuery = text(query).toLowerCase();
  const matches = alphabeticalItems(items)
    .filter((item) => !excluded.has(String(item.id)))
    .filter((item) => !normalizedQuery
      || itemSearchText(item, optionsByItemId, vendorNameById).includes(normalizedQuery))
    .slice(0, PICKER_LIMIT);

  return (
    <div style={styles.picker} data-testid={`${idPrefix}-picker`}>
      <label htmlFor={`${idPrefix}-search`} style={styles.label}>Search product database</label>
      <input
        id={`${idPrefix}-search`}
        type="search"
        className="form-control form-control-sm"
        value={query}
        onChange={(event) => onQueryChange(event.target.value)}
        placeholder="Search name, vendor, unit, category…"
      />
      <div style={styles.pickerList} role="listbox" aria-label="Product search results">
        {matches.map((item) => {
          const selected = String(selectedId || '') === String(item.id);
          const content = (
            <span><ItemMeta item={item} optionsByItemId={optionsByItemId} vendorNameById={vendorNameById} /></span>
          );
          if (selected) {
            return (
              <div
                key={item.id}
                role="option"
                aria-selected="true"
                data-item-id={item.id}
                style={{ ...styles.pickerResult, ...styles.pickerResultSelected, cursor: 'default' }}
              >
                {content}
              </div>
            );
          }
          return (
            <button
              key={item.id}
              type="button"
              role="option"
              aria-selected="false"
              data-item-id={item.id}
              style={styles.pickerResult}
              onClick={() => onPick(item)}
            >
              {content}
            </button>
          );
        })}
      </div>
      {!matches.length && <div style={styles.pickerFooter}>No products match this search.</div>}
      {matches.length === PICKER_LIMIT && (
        <div style={styles.pickerFooter}>Showing the first {PICKER_LIMIT} products. Keep typing to narrow the list.</div>
      )}
    </div>
  );
};

const RelationshipGroupCard = ({
  group,
  variant,
  items,
  itemById,
  optionsByItemId,
  vendorNameById,
  busy,
  onSave,
  onAccept,
  onDismiss,
}) => {
  const originalIds = group.memberIds.map(String);
  const [editing, setEditing] = useState(false);
  const [draftIds, setDraftIds] = useState(originalIds);
  const [detachedOptionIds, setDetachedOptionIds] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [addQuery, setAddQuery] = useState('');
  // '' = accept as proposed (the rows keep their own classification);
  // anything else reclassifies every kept row on confirm.
  const [acceptType, setAcceptType] = useState('');

  useEffect(() => {
    if (editing) return;
    setDraftIds(group.memberIds.map(String));
    setDetachedOptionIds([]);
  }, [editing, group.id, group.memberIds]);
  // A different suggestion never inherits the previous card's reclassify pick.
  useEffect(() => { setAcceptType(''); }, [group.id]);

  const draftMembers = draftIds
    .map((id) => itemById.get(String(id)))
    .filter(Boolean)
    .sort((left, right) => text(left.description).localeCompare(text(right.description), undefined, { sensitivity: 'base', numeric: true }));
  const groupPrices = groupVendorPriceMeta(draftMembers, optionsByItemId, vendorNameById);
  const detachedSet = new Set(detachedOptionIds.map(String));
  const displayedProducts = groupPrices.products.filter(({ row }) => (
    row.optionId == null || !detachedSet.has(String(row.optionId))
  ));
  const dirty = !sameIds(originalIds, draftIds) || detachedOptionIds.length > 0;
  const canSaveExisting = dirty && !busy;
  const canAcceptSuggested = draftMembers.length >= 2 && displayedProducts.length >= 2 && !busy;
  const types = [...new Set(group.rows.map((row) => RELATIONSHIP_TYPE_LABELS[row.relationship_type]).filter(Boolean))];

  const beginEdit = () => {
    setDraftIds(group.memberIds.map(String));
    setDetachedOptionIds([]);
    setEditing(true);
    setAddOpen(false);
    setAddQuery('');
  };
  const cancelEdit = () => {
    setDraftIds(group.memberIds.map(String));
    setDetachedOptionIds([]);
    setEditing(false);
    setAddOpen(false);
    setAddQuery('');
  };
  const finishIfSaved = async (action) => {
    const saved = await action();
    if (saved) cancelEdit();
  };
  const removeDisplayedProduct = (entry) => {
    const itemId = String(entry.item.id);
    const productsForItem = displayedProducts.filter(({ item }) => String(item.id) === itemId);
    // With another vendor product still representing this canonical item, a
    // remove means "extract this vendor product". The save path calls the
    // audited detach RPC and leaves the canonical relationship itself intact.
    if (entry.row.optionId != null && productsForItem.length > 1) {
      setDetachedOptionIds((current) => [...current, String(entry.row.optionId)]);
      return;
    }
    setDraftIds((current) => current.filter((id) => String(id) !== itemId));
    const optionIdsForItem = new Set(
      groupPrices.products
        .filter(({ item }) => String(item.id) === itemId)
        .map(({ row }) => String(row.optionId)),
    );
    setDetachedOptionIds((current) => current.filter((id) => !optionIdsForItem.has(String(id))));
  };

  return (
    <article
      style={{ ...styles.card, ...(variant === 'suggested' ? styles.suggestionCard : {}) }}
      data-testid={`${variant}-relationship-card`}
    >
      <div style={styles.cardHead}>
        <div>
          <h4 style={styles.cardTitle}>{group.title}</h4>
          <div style={styles.cardMeta}>{displayedProducts.length} related product{displayedProducts.length === 1 ? '' : 's'} shown below</div>
          <div style={styles.badgeRow}>
            <span style={{ ...styles.badge, ...(variant === 'suggested' ? styles.suggestionBadge : {}) }}>
              {variant === 'suggested' ? 'Suggested relationship' : 'Confirmed relationship'}
            </span>
            {types.map((label) => <span key={label} style={styles.badge}>{label}</span>)}
            {dirty && <span style={{ ...styles.badge, background: '#FFF1C9', color: '#8A5A00' }}>Edited</span>}
          </div>
        </div>
        <div style={styles.cardActions}>
          {editing ? (
            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={busy} onClick={cancelEdit}>Cancel edit</button>
          ) : (
            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={busy} onClick={beginEdit}>Edit</button>
          )}
          {variant === 'existing' && editing && (
            <button
              type="button"
              className="btn btn-sm btn-primary"
              disabled={!canSaveExisting}
              onClick={() => finishIfSaved(() => onSave(
                group,
                draftMembers.map((item) => item.id),
                detachedOptionIds,
              ))}
            >
              Save changes
            </button>
          )}
          {variant === 'suggested' && (
            <select
              className="form-select form-select-sm"
              style={styles.acceptAsSelect}
              aria-label="Confirm this suggestion as"
              data-testid="accept-as-type"
              value={acceptType}
              disabled={busy}
              onChange={(event) => setAcceptType(event.target.value)}
            >
              <option value="">Accept as proposed</option>
              {RECLASSIFY_OPTIONS
                .filter((option) => option.value !== group.relationshipType)
                .map((option) => (
                  <option key={option.value} value={option.value}>
                    Reclassify: {option.label}
                  </option>
                ))}
            </select>
          )}
          {variant === 'suggested' && (
            <button
              type="button"
              className={`btn btn-sm ${dirty ? 'btn-success' : 'btn-primary'}`}
              disabled={!canAcceptSuggested}
              data-testid="accept-suggested-relationship"
              onClick={() => finishIfSaved(() => onAccept(
                group,
                draftMembers.map((item) => item.id),
                dirty,
                detachedOptionIds,
                acceptType || null,
              ))}
            >
              {dirty ? 'Accept relationship' : 'Accept suggestion'}
            </button>
          )}
          {variant === 'suggested' && (
            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={busy} onClick={() => onDismiss(group)}>Dismiss</button>
          )}
        </div>
      </div>

      <div style={styles.members} aria-label={`Products related under ${group.title}`}>
        {group.hasCollapsedEquivalentConcepts ? draftMembers.map((item, index) => {
          const conceptProducts = displayedProducts.filter(
            (entry) => String(entry.item.id) === String(item.id),
          );
          return (
            <div key={item.id} style={styles.member} data-testid="relationship-member">
              <span style={styles.letter} aria-label={`Product ${relationshipLetter(index)}`}>{relationshipLetter(index)}</span>
              <span style={styles.memberBody}>
                <ItemMeta
                  item={item}
                  optionsByItemId={optionsByItemId}
                  vendorNameById={vendorNameById}
                />
                <span style={styles.itemMeta} data-testid="relationship-concept-buy-count">
                  {conceptProducts.length} {conceptProducts.length === 1 ? 'way' : 'ways'} to buy
                  {conceptProducts.length > 0
                    ? ` · ${conceptProducts.map(({ row }) => row.vendorName).filter(Boolean).join(', ')}`
                    : ''}
                </span>
              </span>
              {editing && (
                <button
                  type="button"
                  className="btn btn-sm btn-link text-danger p-0"
                  disabled={busy}
                  aria-label={`Remove ${text(item.description) || `Item ${item.id}`} from relationship`}
                  onClick={() => setDraftIds((current) => current.filter((id) => String(id) !== String(item.id)))}
                >
                  Remove
                </button>
              )}
            </div>
          );
        }) : displayedProducts.map((entry, index) => {
          const { item, row } = entry;
          const productLabel = [row.vendorName, row.productName]
            .map(text)
            .filter(Boolean)
            .filter((value, valueIndex, values) => values.indexOf(value) === valueIndex)
            .join(' — ')
            || text(item.description)
            || `Item ${item.id}`;
          return (
            <div key={row.key} style={styles.member} data-testid="relationship-member">
              <span style={styles.letter} aria-label={`Product ${relationshipLetter(index)}`}>{relationshipLetter(index)}</span>
              <span style={styles.memberBody}>
                <ItemMeta
                  item={item}
                  optionsByItemId={optionsByItemId}
                  vendorNameById={vendorNameById}
                  priceMeta={{
                    rows: [row],
                    lowestKeys: groupPrices.lowestKeys,
                  }}
                />
              </span>
              {editing && (
                <button
                  type="button"
                  className="btn btn-sm btn-link text-danger p-0"
                  disabled={busy}
                  aria-label={`Remove ${productLabel} from relationship`}
                  onClick={() => removeDisplayedProduct(entry)}
                >
                  Remove
                </button>
              )}
            </div>
          );
        })}
      </div>

      {variant === 'suggested' && group.rows.length > 0 && (
        <div style={styles.reasonList} data-testid="suggestion-reasons">
          {group.rows.map((row) => {
            const nameOf = (id) => text(itemById.get(String(id))?.description) || `Item ${id}`;
            const confidence = Number.parseFloat(row.confidence);
            const why = [text(row.reasoning), text(row.substitution_notes)].filter(Boolean).join(' ');
            return (
              <div key={row.id} style={styles.reason} data-testid="suggestion-reason">
                <div style={styles.reasonPair}>
                  <span>{nameOf(row.item_a_id)} ↔ {nameOf(row.item_b_id)}</span>
                  <span style={{ ...styles.badge, ...styles.suggestionBadge }}>
                    {RELATIONSHIP_TYPE_LABELS[row.relationship_type] || row.relationship_type}
                  </span>
                  {Number.isFinite(confidence) && confidence > 0 && (
                    <span style={styles.reasonConfidence}>{Math.round(confidence * 100)}% confident</span>
                  )}
                </div>
                {why && <div style={styles.reasonText}>{why}</div>}
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <div style={styles.editor} data-testid="relationship-card-editor">
          <div style={styles.editorHead}>
            <div>
              <strong style={{ fontSize: 12 }}>Change this relationship</strong>
              <div style={styles.itemMeta}>
                Remove any vendor product, or add another item. Removing one vendor from a multi-vendor item makes it standalone when saved.
              </div>
            </div>
            <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setAddOpen((open) => !open)}>
              {addOpen ? 'Close picker' : 'Add item'}
            </button>
          </div>
          {draftMembers.length < 2 && (
            <div className="text-danger small mt-2">
              {variant === 'existing'
                ? 'Saving now will dissolve this relationship.'
                : 'A relationship needs at least two products.'}
            </div>
          )}
          {addOpen && (
            <RelationshipItemPicker
              idPrefix={`${variant}-${group.id.replace(/[^a-z0-9_-]/gi, '-')}`}
              items={items}
              optionsByItemId={optionsByItemId}
              vendorNameById={vendorNameById}
              query={addQuery}
              onQueryChange={setAddQuery}
              selectedId=""
              excludeIds={draftIds}
              onPick={(item) => {
                setDraftIds((current) => [...current, String(item.id)]);
                setAddQuery('');
                setAddOpen(false);
              }}
            />
          )}
        </div>
      )}
    </article>
  );
};

export default function ManageRelationshipsView({
  storeId,
  items = [],
  relationships = [],
  optionsByItemId = {},
  comparisonItemById = {},
  vendorNameById = {},
  storedCanonicalByGroupKey = {},
  onChanged,
  onBackToProducts,
}) {
  const [activeTab, setActiveTab] = useState('existing');
  const [existingQuery, setExistingQuery] = useState('');
  const [suggestedQuery, setSuggestedQuery] = useState('');
  const [busyKey, setBusyKey] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [leftQuery, setLeftQuery] = useState('');
  const [rightQuery, setRightQuery] = useState('');
  const [leftItemId, setLeftItemId] = useState('');
  const [rightItemId, setRightItemId] = useState('');
  const [createType, setCreateType] = useState('purchasing_alternative');

  const sortedItems = useMemo(() => alphabeticalItems(items.map((item) => ({
    ...item,
    ...(comparisonItemById?.[item.id] || comparisonItemById?.[String(item.id)] || {}),
  }))), [comparisonItemById, items]);
  const equivalentGroups = useMemo(
    () => buildWorkingSetGroups(relationships),
    [relationships],
  );
  const projectedOptionsByItemId = useMemo(
    () => expandOptionsAcrossGroup(optionsByItemId, equivalentGroups),
    [optionsByItemId, equivalentGroups],
  );
  // Pair-level figures for the headline, over the SAME rows this workspace
  // renders. Confirmed equivalences are Related Products and are excluded
  // from both the cards and these numbers — a hidden row that still inflates
  // a badge is the exact lie this change exists to remove. Cards group pairs
  // for scanning, so "19 cards" and "19 relationships" remain different
  // claims and are reported separately.
  const metrics = useMemo(() => secondaryRelationshipMetrics(relationships), [relationships]);
  // Every confirmed group of every type. This is the LOGIC list, not the
  // display list: suggestion suppression ("do not re-ask a pair already
  // answered") and the Create pickers' exclusions must still see confirmed
  // equivalences even though no card renders them.
  const allConfirmedGroups = useMemo(
    () => buildRelationshipGroups(relationships, sortedItems, 'confirmed', {
      optionsByItemId,
      storedCanonicalByGroupKey,
    }),
    [relationships, sortedItems, optionsByItemId, storedCanonicalByGroupKey],
  );
  // The DISPLAY list: substitutes and family links only.
  const existingGroups = useMemo(
    () => allConfirmedGroups.filter((group) => isSecondaryWorkspaceType(group.relationshipType)),
    [allConfirmedGroups],
  );
  // itemId -> the SET of confirmed groups it belongs to. Groups are built per
  // relationship_type now, so one product is routinely in two of them (an
  // alternative group and a same-family group) and a single-value map would
  // silently keep whichever came last.
  const confirmedGroupIdsByItemId = useMemo(() => {
    const map = new Map();
    allConfirmedGroups.forEach((group) => {
      group.memberIds.forEach((itemId) => {
        const key = String(itemId);
        if (!map.has(key)) map.set(key, new Set());
        map.get(key).add(group.id);
      });
    });
    return map;
  }, [allConfirmedGroups]);
  const suggestedGroups = useMemo(
    () => buildRelationshipGroups(
      relationships.filter((row) => {
        if (row.status !== 'suggested') return true;
        // Already confirmed together under an EQUAL-OR-NARROWER type — do not
        // re-ask. A broader confirmed link never hides a narrower suggestion:
        // a confirmed same-family pair still shows its alternative proposal,
        // and a confirmed alternative pair still shows an equivalent one.
        const left = confirmedGroupIdsByItemId.get(String(row.item_a_id));
        const right = confirmedGroupIdsByItemId.get(String(row.item_b_id));
        if (!left || !right) return true;
        const suggestedRank = TYPE_RANK[row.relationship_type] ?? 0;
        return !allConfirmedGroups.some((group) => (
          left.has(group.id) && right.has(group.id)
          && (TYPE_RANK[group.relationshipType] ?? 0) >= suggestedRank
        ));
      }),
      sortedItems,
      'suggested',
      { optionsByItemId, storedCanonicalByGroupKey },
    ),
    [
      confirmedGroupIdsByItemId, allConfirmedGroups, relationships, sortedItems,
      optionsByItemId, storedCanonicalByGroupKey,
    ],
  );
  // Count the suggestions this view will actually SHOW. metrics counts every
  // pending pair in the store, but suggestedGroups drops the ones already
  // implied by a confirmed group — offering "Review 1 suggestion" and then an
  // empty queue is the same class of lie this change exists to remove.
  const suggestedPairCount = useMemo(() => {
    const pairs = new Set();
    suggestedGroups.forEach((group) => group.rows.forEach((row) => {
      pairs.add(pairKey(row.item_a_id, row.item_b_id));
    }));
    return pairs.size;
  }, [suggestedGroups]);
  const itemById = useMemo(() => {
    const map = new Map(sortedItems.map((item) => [String(item.id), item]));
    [...allConfirmedGroups, ...suggestedGroups].forEach((group) => {
      group.members.forEach((item) => map.set(String(item.id), item));
    });
    return map;
  }, [allConfirmedGroups, sortedItems, suggestedGroups]);

  const filterGroups = (groups, query) => {
    const normalized = text(query).toLowerCase();
    if (!normalized) return groups;
    return groups.filter((group) => group.members.some((item) => (
      itemSearchText(item, optionsByItemId, vendorNameById).includes(normalized)
    )));
  };
  const visibleExisting = filterGroups(existingGroups, existingQuery);
  const visibleSuggested = filterGroups(suggestedGroups, suggestedQuery);
  // Everything already related to this product, across every type of
  // relationship it takes part in — the Create pickers must not offer a pair
  // that already exists, and an item can now sit in more than one group.
  const confirmedGroupMemberIds = (itemId) => {
    if (!itemId) return [];
    const groupIds = confirmedGroupIdsByItemId.get(String(itemId));
    if (!groupIds) return [];
    const members = new Set();
    allConfirmedGroups.forEach((group) => {
      if (!groupIds.has(group.id)) return;
      group.memberIds.forEach((memberId) => members.add(memberId));
    });
    return [...members];
  };

  const runMutation = async (key, action, successMessage) => {
    setBusyKey(key);
    setError('');
    setNotice('');
    try {
      await action();
      if (typeof onChanged === 'function') await onChanged();
      setNotice(successMessage);
      return true;
    } catch (err) {
      setError(err?.message || 'The relationship could not be updated.');
      return false;
    } finally {
      setBusyKey('');
    }
  };

  // Every pair that already has a row of ANY type. record_item_relationship
  // upserts on the UNIQUE (store_id, item_a_id, item_b_id) pair, so writing a
  // "missing" pair that in fact exists under another type OVERWRITES that
  // relationship. group.rows is type-scoped, so it cannot see the collision on
  // its own — this map has to come from the full relationship list.
  const existingPairKeys = useMemo(() => {
    const keys = new Set();
    (Array.isArray(relationships) ? relationships : []).forEach((row) => {
      if (row?.item_a_id == null || row?.item_b_id == null) return;
      keys.add(pairKey(row.item_a_id, row.item_b_id));
    });
    return keys;
  }, [relationships]);

  const connectDraftMembers = async (group, draftIds, rowsKept, typeOverride = null) => {
    const draftItems = draftIds.map((id) => itemById.get(String(id))).filter(Boolean);
    const anchorId = draftItems[0]?.id;
    if (!anchorId) return;
    const directPairs = new Set(rowsKept.map((row) => pairKey(row.item_a_id, row.item_b_id)));
    const relationshipType = (STORED_RELATIONSHIP_TYPES.includes(typeOverride) && typeOverride)
      || relationshipTypeForGroup(group);
    for (const item of draftItems.slice(1)) {
      const key = pairKey(anchorId, item.id);
      if (directPairs.has(key)) continue;
      // Already related under some other type — leave that row alone rather
      // than retyping it as a side effect of editing this card.
      if (existingPairKeys.has(key)) continue;
      await createManualRelationship(storeId, anchorId, item.id, relationshipType);
    }
  };

  const saveExistingGroup = (group, draftIds, detachedOptionIds = []) => runMutation(
    `save:${group.id}`,
    async () => {
      for (const optionId of detachedOptionIds) await detachOptionToNewItem(optionId);
      const draftSet = new Set(draftIds.map(String));
      const projectedId = (rawId) => group.conceptIdByRawItemId?.[String(rawId)] ?? String(rawId);
      const rowsKept = group.rows.filter((row) => (
        draftSet.has(String(projectedId(row.item_a_id)))
        && draftSet.has(String(projectedId(row.item_b_id)))
      ));
      const rowsRemoved = group.rows.filter((row) => !rowsKept.includes(row));
      for (const row of rowsRemoved) await rejectRelationship(row.id);
      await connectDraftMembers(group, draftIds, rowsKept);
    },
    'Relationship updated.',
  );

  const acceptSuggestedGroup = (group, draftIds, edited, detachedOptionIds = [], typeOverride = null) => runMutation(
    `accept:${group.id}`,
    async () => {
      for (const optionId of detachedOptionIds) await detachOptionToNewItem(optionId);
      const draftSet = new Set(draftIds.map(String));
      const projectedId = (rawId) => group.conceptIdByRawItemId?.[String(rawId)] ?? String(rawId);
      const rowsKept = group.rows.filter((row) => (
        draftSet.has(String(projectedId(row.item_a_id)))
        && draftSet.has(String(projectedId(row.item_b_id)))
      ));
      const rowsRemoved = group.rows.filter((row) => !rowsKept.includes(row));
      for (const row of rowsKept) {
        // Confirmation respects classification: a plain accept confirms the
        // row AS its proposed type (the resolve RPC keeps the stored type
        // when no override is sent); a reclassify confirms every kept row as
        // the type the person chose instead.
        if (typeOverride && typeOverride !== row.relationship_type) {
          await confirmRelationship(row.id, typeOverride);
        } else {
          await confirmRelationship(row.id);
        }
      }
      for (const row of rowsRemoved) await rejectRelationship(row.id);
      if (edited) await connectDraftMembers(group, draftIds, rowsKept, typeOverride);
    },
    'Relationship accepted and added to Existing relationships.',
  );

  const dismissSuggestedGroup = (group) => runMutation(
    `dismiss:${group.id}`,
    async () => {
      for (const row of group.rows) await rejectRelationship(row.id);
    },
    'Suggestion dismissed.',
  );

  const createRelationship = () => {
    if (!leftItemId || !rightItemId || String(leftItemId) === String(rightItemId)) return Promise.resolve(false);
    const left = itemById.get(String(leftItemId));
    const right = itemById.get(String(rightItemId));
    return runMutation(
      'create',
      () => createManualRelationship(storeId, leftItemId, rightItemId, createType),
      `${text(left?.description) || 'The first product'} and ${text(right?.description) || 'the second product'} are now related.`,
    ).then((saved) => {
      if (saved) {
        setLeftItemId('');
        setRightItemId('');
        setLeftQuery('');
        setRightQuery('');
        setActiveTab('existing');
      }
      return saved;
    });
  };

  return (
    <div style={styles.shell} data-testid="relationships-workspace">
      <div style={styles.intro}>
        <h3 style={styles.title}>
          Substitutes &amp; alternatives
          <HelpInfoButton
            helpTopicId="catalog.relationship_suggestions"
            page="catalog"
            section="catalog_tabs"
            context={{ active_relationship_tab: activeTab }}
          />
        </h3>
        <p style={styles.hint}>
          {activeTab === 'suggested'
            ? 'Proposals Countertop has not confirmed. Nothing here affects counting, ordering, or price comparison until you accept it.'
            : 'Different products that can stand in for each other — a substitute when your usual one is out, or a cheaper equivalent. This is NOT the same as one item bought from several vendors; that lives under Related products.'}
        </p>

        {/* Headline figures count RELATIONSHIPS (item pairs), the same unit the
            import review reports, so the two screens can be compared. Cards are
            a display grouping and are counted separately in the toolbar. */}
        <div style={styles.headline}>
          <span style={styles.headlineFigure}>
            <span style={styles.headlineNumber}>{metrics.confirmedRelationshipCount}</span>
            confirmed relationship{metrics.confirmedRelationshipCount === 1 ? '' : 's'}
          </span>
          <span style={styles.headlineFigure}>
            <span style={styles.headlineNumber}>{metrics.participatingItemCount}</span>
            product{metrics.participatingItemCount === 1 ? '' : 's'} involved
          </span>
          {suggestedPairCount > 0 && activeTab !== 'suggested' && (
            <button
              type="button"
              style={styles.reviewCta}
              data-testid="review-suggestions-cta"
              onClick={() => {
                setActiveTab('suggested');
                setError('');
                setNotice('');
              }}
            >
              Review {suggestedPairCount} suggestion{suggestedPairCount === 1 ? '' : 's'} →
            </button>
          )}
        </div>

        {typeof onBackToProducts === 'function' && (
          <div style={{ marginTop: 10 }}>
            <button type="button" style={styles.backLink} data-testid="back-to-related-products" onClick={onBackToProducts}>
              ← Back to related products
            </button>
          </div>
        )}
        <div style={styles.tabs} role="tablist" aria-label="Substitute and alternative views">
          {[
            ['existing', 'Relationships', existingGroups.length],
            ['create', 'Create relationship', null],
          ].map(([value, label, count]) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={activeTab === value}
              style={{ ...styles.tab, ...(activeTab === value ? styles.activeTab : {}) }}
              onClick={() => {
                setActiveTab(value);
                setError('');
                setNotice('');
              }}
            >
              {label}{count !== null && <span style={styles.count}>{count}</span>}
            </button>
          ))}
          {activeTab === 'suggested' && (
            <button
              type="button"
              role="tab"
              aria-selected
              style={{ ...styles.tab, ...styles.activeTab }}
            >
              Suggestions<span style={styles.count}>{suggestedGroups.length}</span>
            </button>
          )}
        </div>
      </div>

      {error && <div style={styles.error} role="alert">{error}</div>}
      {notice && <div style={styles.notice} role="status">{notice}</div>}

      {activeTab === 'existing' && (
        <section aria-label="Existing relationships">
          <div style={styles.toolbar}>
            <div style={styles.searchWrap}>
              <label htmlFor="existing-relationship-search" style={styles.label}>Search existing relationships</label>
              <input
                id="existing-relationship-search"
                type="search"
                className="form-control"
                value={existingQuery}
                onChange={(event) => setExistingQuery(event.target.value)}
                placeholder="Search product name, vendor, unit, category…"
              />
            </div>
            <div style={styles.summary}>{visibleExisting.length} of {existingGroups.length} relationship group{existingGroups.length === 1 ? '' : 's'}</div>
          </div>
          <div style={{ height: 10 }} />
          {!existingGroups.length ? (
            <div style={styles.empty}>
              <strong>No existing relationships yet.</strong>
              <div className="mt-1">Review a suggestion or create the first relationship.</div>
              <button type="button" className="btn btn-sm btn-primary mt-3" onClick={() => setActiveTab('create')}>Create relationship</button>
            </div>
          ) : !visibleExisting.length ? (
            <div style={styles.empty}>No existing relationships match that search.</div>
          ) : (
            // Alternative products, Same family, and Equivalent groupings are
            // three different claims (domain model), so each renders under its
            // own labelled heading — a family link never sits among the
            // alternative cards as if it promised substitution.
            <div style={styles.sections}>
              {EXISTING_SECTIONS.map(([relationshipType, heading, hint]) => {
                const sectionGroups = visibleExisting
                  .filter((group) => group.relationshipType === relationshipType);
                if (!sectionGroups.length) return null;
                return (
                  <section
                    key={relationshipType}
                    aria-label={heading}
                    data-testid={`relationship-section-${relationshipType}`}
                  >
                    <h4 style={styles.sectionHeading}>
                      {heading}
                      <span style={styles.sectionCount}>{sectionGroups.length}</span>
                    </h4>
                    <p style={styles.sectionHint}>{hint}</p>
                    <div style={styles.list}>
                      {sectionGroups.map((group) => (
                        <RelationshipGroupCard
                          key={group.id}
                          group={group}
                          variant="existing"
                          items={sortedItems}
                          itemById={itemById}
                          optionsByItemId={group.hasCollapsedEquivalentConcepts
                            ? projectedOptionsByItemId : optionsByItemId}
                          vendorNameById={vendorNameById}
                          busy={busyKey === `save:${group.id}`}
                          onSave={saveExistingGroup}
                        />
                      ))}
                    </div>
                  </section>
                );
              })}
            </div>
          )}
        </section>
      )}

      {activeTab === 'suggested' && (
        <section aria-label="Suggested relationships">
          <div style={styles.suggestionIntro}>
            <strong>These are proposals, not relationships.</strong>{' '}
            Countertop thinks these products may do the same job. Accepting one adds it to your
            relationships; dismissing it stops the pair being suggested again.
            <div className="mt-2">
              <button type="button" style={styles.backLink} onClick={() => { setActiveTab('existing'); setError(''); setNotice(''); }}>
                ← Back to your relationships
              </button>
            </div>
          </div>
          <div style={styles.toolbar}>
            <div style={styles.searchWrap}>
              <label htmlFor="suggested-relationship-search" style={styles.label}>Search suggestions</label>
              <input
                id="suggested-relationship-search"
                type="search"
                className="form-control"
                value={suggestedQuery}
                onChange={(event) => setSuggestedQuery(event.target.value)}
                placeholder="Search product name, vendor, unit, category…"
              />
            </div>
            <div style={styles.summary}>{visibleSuggested.length} of {suggestedGroups.length} suggestion group{suggestedGroups.length === 1 ? '' : 's'}</div>
          </div>
          <div style={{ height: 10 }} />
          {!suggestedGroups.length ? (
            <div style={styles.empty}>No suggested relationships are waiting for review.</div>
          ) : !visibleSuggested.length ? (
            <div style={styles.empty}>No suggested relationships match that search.</div>
          ) : (
            <div style={styles.list}>
              {visibleSuggested.map((group) => (
                <RelationshipGroupCard
                  key={group.id}
                  group={group}
                  variant="suggested"
                  items={sortedItems}
                  itemById={itemById}
                  optionsByItemId={group.hasCollapsedEquivalentConcepts
                    ? projectedOptionsByItemId : optionsByItemId}
                  vendorNameById={vendorNameById}
                  busy={busyKey === `accept:${group.id}` || busyKey === `dismiss:${group.id}`}
                  onAccept={acceptSuggestedGroup}
                  onDismiss={dismissSuggestedGroup}
                />
              ))}
            </div>
          )}
        </section>
      )}

      {activeTab === 'create' && (
        <section style={styles.createCard} aria-label="Create relationship">
          <h4 style={styles.cardTitle}>Start a new relationship</h4>
          <p style={styles.hint}>Choose a product on each side. Both searchable lists default alphabetically and show the vendor and unit.</p>
          <div style={styles.createGrid}>
            <div>
              <div style={styles.label}>Product A</div>
              <RelationshipItemPicker
                idPrefix="create-left"
                items={sortedItems}
                optionsByItemId={optionsByItemId}
                vendorNameById={vendorNameById}
                query={leftQuery}
                onQueryChange={setLeftQuery}
                selectedId={leftItemId}
                excludeIds={[rightItemId, ...confirmedGroupMemberIds(rightItemId)]}
                onPick={(item) => setLeftItemId(String(item.id))}
              />
            </div>
            <div>
              <div style={styles.label}>Product B</div>
              <RelationshipItemPicker
                idPrefix="create-right"
                items={sortedItems}
                optionsByItemId={optionsByItemId}
                vendorNameById={vendorNameById}
                query={rightQuery}
                onQueryChange={setRightQuery}
                selectedId={rightItemId}
                excludeIds={[leftItemId, ...confirmedGroupMemberIds(leftItemId)]}
                onPick={(item) => setRightItemId(String(item.id))}
              />
            </div>
          </div>
          <div style={styles.createFooter}>
            <div style={{ minWidth: 220 }}>
              <label htmlFor="create-relationship-type" style={styles.label}>Relationship type</label>
              <select
                id="create-relationship-type"
                className="form-select form-select-sm"
                value={createType}
                onChange={(event) => setCreateType(event.target.value)}
              >
                {TYPE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </div>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!leftItemId || !rightItemId || busyKey === 'create'}
              onClick={createRelationship}
            >
              {busyKey === 'create' ? 'Creating…' : 'Create relationship'}
            </button>
          </div>
        </section>
      )}
    </div>
  );
}
